<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { apiGet, apiPut } from "../api";
import { useToast } from "../stores/toast";

interface SiteSettings { instagramUrl: string; tiktokUrl: string; showLikes: boolean; accountTeaser: string }

const toast = useToast();
const form = reactive<SiteSettings>({ instagramUrl: "", tiktokUrl: "", showLikes: true, accountTeaser: "" });
const loading = ref(true);
const saving = ref(false);
const error = ref("");

async function load() {
  try {
    Object.assign(form, await apiGet<SiteSettings>("/settings"));
  } catch (e) { error.value = String(e); } finally { loading.value = false; }
}
async function save() {
  error.value = ""; saving.value = true;
  try {
    Object.assign(form, await apiPut<SiteSettings>("/settings", { ...form }));
    toast.show("Instellingen opgeslagen");
  } catch (e) { error.value = `Opslaan mislukt: ${(e as Error).message}`; } finally { saving.value = false; }
}
onMounted(load);
</script>

<template>
  <div class="wrap">
    <div class="phead">
      <div>
        <h1 class="title">Instellingen</h1>
        <p class="info">Algemene winkelinstellingen — meteen zichtbaar in de storefront.</p>
      </div>
      <button class="btn primary" :disabled="saving || loading" @click="save">{{ saving ? "Opslaan…" : "Opslaan" }}</button>
    </div>

    <p v-if="error" class="err">{{ error }}</p>
    <p v-if="loading" class="muted">Laden…</p>

    <section v-else class="card">
      <h2 class="ct">Social media</h2>
      <label class="fl">Instagram-URL</label>
      <input v-model="form.instagramUrl" class="inp" placeholder="https://instagram.com/binker" />
      <label class="fl">TikTok-URL</label>
      <input v-model="form.tiktokUrl" class="inp" placeholder="https://tiktok.com/@binker" />
      <small class="hint">Leeg = het icoon wordt verborgen in de winkelbalk.</small>

      <h2 class="ct sp">Winkelbalk</h2>
      <label class="check"><input type="checkbox" v-model="form.showLikes" /> Hartje (vind-ik-leuk) tonen</label>

      <h2 class="ct sp">Account</h2>
      <label class="fl">Tekst in het account-uitklapvenster</label>
      <textarea v-model="form.accountTeaser" class="inp" rows="2" placeholder="Nog even geduld, hier kun je straks een account aanmaken." />
      <small class="hint">Verschijnt als bezoekers op het avatar-rondje klikken (account aanmaken volgt later).</small>
    </section>
  </div>
</template>

<style scoped>
.wrap { max-width: 620px; }
.phead { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0 0 6px; }
.info { color: var(--mid); font-size: 13px; margin: 0; }
.muted { color: var(--muted); }
.err { color: var(--red); background: var(--red-bg); padding: 10px 14px; border-radius: 10px; }
.btn { border: 0; border-radius: 999px; padding: 10px 18px; font-weight: 700; font-size: 14px; cursor: pointer; }
.btn.primary { background: var(--coral); color: #fff; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.card { background: var(--surface); border: 1px solid var(--hairline); border-radius: 14px; padding: 18px; }
.ct { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); margin: 0 0 10px; }
.ct.sp { margin-top: 24px; }
.fl { display: block; font-size: 12px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.04em; margin: 12px 0 6px; }
.inp { width: 100%; padding: 10px 12px; border: 1px solid var(--border-input); border-radius: 10px; background: var(--surface); font-size: 14px; font-family: inherit; }
.hint { display: block; color: var(--muted); font-size: 12px; margin-top: 6px; }
.check { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; }
</style>
